import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {AppStateType} from "../../../../../redux/reducers";
import React, {FC, useEffect, useRef, useState} from "react";
import {deleteUnitImage, fetchUnitImages, partialUpdateUnitImage} from "../../../../../redux/actions/unitImagesActions";
import axios from "axios";
import {createUnitImage} from "../../../../../services/imageService";
import CheckIcon from "@material-ui/icons/Check";
import {Box, Card as MuiCard, CardContent, makeStyles, withStyles} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import RenderImageComponent from "./RenderImageComponent";
import Snackbar from "@material-ui/core/Snackbar";
import {DropzoneArea} from "material-ui-dropzone";
import styled from "styled-components/macro";
import {spacing} from "@material-ui/system";
import PictureGroup from "./PictureGroup";
import Swal from "sweetalert2";
import {A11yUnit} from "../../../../../models/utils";
import {AccessibilityUnit} from "../../../../../models/AccessibilityUnits";

const useStyles = makeStyles(() => ({
  snackbar: {
    backgroundColor: "#354557",
    color: "#cfcfcf",
    width: "400px"
  },
  dropzoneRoot: {
    minHeight: "175px",
    border: "2px dashed rgba(132, 250, 176, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 40px",
  },
}));

const StyledDropzoneArea = withStyles({
  root: {
    border: "2px dashed rgba(132, 250, 176, 0.4)",
  },
})(DropzoneArea)
const Spacer = styled.div(spacing);
const Card = styled(MuiCard)`
  overflow-y: auto !important;
`

type DropZoneType = {
  unit: AccessibilityUnit,
  user: any,
  unitStandard: A11yUnit,
  a11y: any,
  handleTypeChange: any,
  handleClose: () => void,
  onLoading: (open: boolean, cancel?: boolean) => void,
  onSetAbortController: (controller: AbortController) => void
}

const DefaultDropzone: FC<DropZoneType> = ({
  unit,
  user,
  unitStandard,
  handleTypeChange,
  onLoading,
  onSetAbortController
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation();
  const classes = useStyles();
  const dropzoneRef = useRef<any>(null!);
  const img: any = useSelector((state: AppStateType) => state.unitImagesReducer.unitImages)
  const [images, setImages] = useState<any[]>([])
  const [files, setFiles] = useState<any[]>([])
  const [key, setKey] = useState(0)
  const [snackbarStatus, setSnackbarStatus] = useState<null | 'success' | 'error'>(null);

  const handleEdit = async (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    onLoading(true)

    const files = event.currentTarget.files!;
    if (files.length > 0) {
      const image = files[0];
      const imageIndex = img.findIndex((image: any) => image.id === id)
      const data = {
        image
      }
      await dispatch(partialUpdateUnitImage(imageIndex, id, data, user.token))
    }
    onLoading(false)
  }

  const handleGeneralImage = (id: string) => {
    const imageIndex = img.findIndex((image: any) => image.id === id)
    if (imageIndex !== -1) {
      dispatch(partialUpdateUnitImage(
        imageIndex,
        id,
        {is_general_image: !img[imageIndex].is_general_image},
        user.token
      ))
    }
  }

  const handleImageDownload = async (files: any, index: number) => {
    const imageIndex = img.findIndex((image: any) => image.id === index);
    const file = files[imageIndex];
    const imageName = file.image.substring(file.image.lastIndexOf("/") + 1);

    axios({
      url: `${file.image}?not-from-cache-please`,
      method: "GET",
      responseType: "blob",
      withCredentials: false,
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", imageName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handleDelete = async (id: string) => {
    const imageIndex = img.findIndex((image: any) => image.id === id)

    const getFileName = (img: any[], imageIndex: number) => {
      if (img[imageIndex]) {
        const { image } = img[imageIndex]
        const splitImageURL = image.split('/')
        return splitImageURL[splitImageURL.length - 1]
      }
      return 'image'
    }

    Swal.fire({
      title: t('imagesPage_Are_your_sure'),
      text: `${t('imagesPage_Delete')} '${getFileName(img, imageIndex)}'?`,
      confirmButtonText: t('imagesPage_Yes'),
      cancelButtonText: t('imagesPage_Cancel'),
      showCancelButton: true,
      reverseButtons: true,
      width: '400px'
    }).then(async (result) => {
      if (result.isConfirmed) {
        onLoading(true)
        await dispatch(deleteUnitImage(id, user.token, imageIndex))
        onLoading(false)
      }
    })
  }

  const onSaveImages = () => {
    setSnackbarStatus(null);
    onLoading(true, true)
    let promisesChain: any = []

    const abortController = new AbortController()
    onSetAbortController(abortController)
    images.forEach((item: any, index: any) => {
      const data = {
        // image: item.imageDataUrl,
        image: files[index],
        accessibility_unit: unit.id,
        accessibility: item.accessibility
      }
      if (!data.accessibility) {
        delete data.accessibility;
      }
      const promise = createUnitImage.call(null, data, user.token, abortController)
      promisesChain.push(promise)
    })
    Promise.all(promisesChain).then(() => {
      setFiles([])
      setImages([])
      setKey(key + 1)
      if (unit.id) {
        dispatch(fetchUnitImages(unit.id, user.token))
      }
      setSnackbarStatus('success');
      onLoading(false)
    }).catch(() => {
      setFiles([])
      setImages([])
      setKey(key + 1)
      if (unit.id) {
        dispatch(fetchUnitImages(unit.id, user.token))
      }
      setSnackbarStatus('error');
      onLoading(false)
    })
  }

  const getBase64 = (file: any) => {
    return new Promise(resolve => {
      const reader = new FileReader()
      reader.onload = (ev: any) => {
        resolve(ev.target.result)
      }
      reader.readAsDataURL(file)
    })
  }

  const onChange = (files: any) => {
    let filesArr = files.map((file: any) => {
      return getBase64(file)
    })
    Promise.all(filesArr).then((dataUrls) => {
      let images: any[] = []
      dataUrls.forEach((item: any) => {
        const imageData = {
          accessibility: "",
          imageDataUrl: item
        }
        images.push(imageData)
      })
      setFiles(files)
      setImages(images)
    })
  }

  useEffect(() => {
    if (images.length > 0) {
      onSaveImages()
    }
  }, [images])

  const getSnackbarMessage = (status: 'error' | 'success') => {
    switch (status) {
      case 'success':
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <CheckIcon style={{ color: "green" }} />
            <span style={{ marginLeft: "5px" }}>{t("questionUnits_uploadSuccess")}</span>
          </div>
        )
      case 'error':
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{t("questionUnits_uploadPictures_uploadError")}</span>
          </div>
        )
      default:
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{t("questionUnits_uploadPictures_uploadError")}</span>
          </div>
        )
    }
  }

  return (
    <Card>
      <CardContent>
        <PictureGroup
          images={img}
          mandatory={true}
          mainUnitImage={true}
          unitStandardValues={unitStandard.values}
        />
        <PictureGroup
          images={img}
          mandatory={false}
          unitStandardValues={unitStandard.values}
        />
        <Spacer mb={4} />
        <StyledDropzoneArea
          filesLimit={4}
          showAlerts={['error', 'info']}
          acceptedFiles={['image/*']}
          dropzoneClass={classes.dropzoneRoot}
          showFileNamesInPreview={true}
          showFileNames={true}
          onChange={onChange}
          maxFileSize={10000000}
          dropzoneText={files.length > 0 ? "" : t("unitImagesDialog_dropzone")}
          Icon={files.length > 0 ? () => <div /> : CloudUploadIcon as any}
          innerRef={dropzoneRef}
          key={key}
        />
        <Spacer mb={5} />
        <Box p={2} display="flex">
          {img.length ? <RenderImageComponent
            images={img}
            unitStandardValues={unitStandard.values}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            handleDownload={handleImageDownload}
            onChange={handleTypeChange}
            onHandleGeneralImage={handleGeneralImage}
          /> : null}
        </Box>
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          open={snackbarStatus === 'error'}
          message={getSnackbarMessage('error')}
          onClose={() => setSnackbarStatus(null)}
          autoHideDuration={3500}
          ContentProps={{ className: classes.snackbar }}
        />
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          open={snackbarStatus === 'success'}
          message={getSnackbarMessage('success')}
          onClose={() => setSnackbarStatus(null)}
          autoHideDuration={3500}
          ContentProps={{ className: classes.snackbar }}
        />
      </CardContent>
    </Card>
  );
}

export default DefaultDropzone